<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue';

import type { FlashMessageItemProps } from '@shop/components/ui/FlashMessageItem.types';

const props = withDefaults(defineProps<FlashMessageItemProps>(), {
  timeout: 10000,
});

const emit = defineEmits<{
  dismiss: [id: number];
}>();

const fontIcon = computed(() => {
  if (props.level === 'success') {
    return 'check';
  }
  if (props.level === 'error') {
    return 'cancel';
  }
  return null;
});

const badgeLabel = computed(() => {
  if (props.level === 'warning') {
    return '!';
  }
  if (props.level === 'info') {
    return 'i';
  }
  return null;
});

const needsAttention = computed(() => props.level === 'error' || props.level === 'warning');

const a11yRole = computed(() => (needsAttention.value ? 'alert' : 'status'));

let timer: ReturnType<typeof setTimeout> | undefined;

function dismiss(): void {
  if (timer !== undefined) {
    clearTimeout(timer);
    timer = undefined;
  }
  emit('dismiss', props.id);
}

onMounted(() => {
  if (props.timeout > 0) {
    timer = setTimeout(() => {
      emit('dismiss', props.id);
    }, props.timeout);
  }
});

onBeforeUnmount(() => {
  if (timer !== undefined) {
    clearTimeout(timer);
  }
});
</script>

<template>
  <div
    class="flashmessage__message"
    :class="[
      `flashmessage__message--${level}`,
      { 'flashmessage__message--attention': needsAttention },
    ]"
    :role="a11yRole"
    @click="dismiss"
  >
    <span
      v-if="badgeLabel"
      class="flashmessage__badge"
      :class="`flashmessage__badge--${level}`"
      aria-hidden="true"
    >
      {{ badgeLabel }}
    </span>
    <i
      v-else-if="fontIcon"
      class="icon icon--showleft flashmessage__icon"
      :class="`icon--${fontIcon}`"
      aria-hidden="true"
    />
    <span class="flashmessage__text">{{ message }}</span>
    <button type="button" class="flashmessage__close" aria-label="Sluiten" @click.stop="dismiss">
      <i class="icon icon--cancel icon--showleft" aria-hidden="true" />
    </button>
  </div>
</template>
